import { useState } from 'react';
import { useTheme } from 'styled-components';
import Alert from '../../../../common/base/Alert';
import Button from '../../../../common/base/Button';
import Text from '../../../../common/base/Text';
import Tabs from '../../../../common/base/Tabs';
import { Div } from '../../../../common/helpers/StyledUtils';
import CopyableField from '../../../../common/CopyableField';
import { useToasts } from '../../../../common/Toast';
import { CLIOnboardingContext, revealGuidedCliOnboardingPostConnectSteps } from '../GuidedCLIOnboarding';
import { StyledRadioCard } from '../../../../common/Form/Fields/RadioCardsInput';
import Badge from '../../../../common/base/Badge';
import { StyledCardSection } from '../../../../common/base/Card';
import { StyledCard } from '../../../../common/base/Card';
import Editor from '../../../../common/Editor';
interface Props {
  context: CLIOnboardingContext;
  mutateContext: (input: any) => void;
  nextStep: () => void;
  is_current_step: boolean;
}
type TriggerOption = 'copy_url' | 'mock_request';
const TriggerEvent: React.FC<Props> = ({
  context,
  nextStep
}) => {
  const theme = useTheme();
  const {
    addToast
  } = useToasts();
  const {
    source,
    cli_connected
  } = context;
  const trigger_step_revealed = revealGuidedCliOnboardingPostConnectSteps(context);
  const show_cli_disconnected_warning = trigger_step_revealed && !cli_connected;
  const [trigger_option, setTriggerOption] = useState<TriggerOption>('copy_url');
  const [request_format, setRequestFormat] = useState<'web_ui' | 'curl'>('web_ui');
  const [is_sending, setIsSending] = useState(false);
  const [mock_body, setMockBody] = useState(JSON.stringify({
    hello: 'world'
  }, null, 2));
  const source_url = source?.url || 'https://your-source.hookdeck.com';
  const curl_command = `curl -X POST ${source_url} \\
  -H "Content-Type: application/json" \\
  -d '{"hello": "world"}'`;
  const sendMockRequest = async () => {
    if (!source) return;
    setIsSending(true);
    try {
      await fetch(source.url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: mock_body
      });
      addToast('success', 'Mock request sent successfully');
      nextStep();
    } catch (error) {
      addToast('error', 'Failed to send mock request');
    } finally {
      setIsSending(false);
    }
  };
  return <Div>
      {show_cli_disconnected_warning && <Alert warning inline m={{
      b: 4
    }}>
          <Text>
            The Hookdeck CLI is not connected. Run{' '}
            <Text as="span" mono>
              hookdeck listen
            </Text>{' '}
            again so new events are forwarded to your local server. You can still send requests to
            Hookdeck, but they will not reach your machine until the CLI is running.
          </Text>
        </Alert>}
      <Div flex={{
      gap: 4
    }} m={{
      b: 8
    }}>
        <StyledRadioCard as="label" p={3} checked={trigger_option === 'copy_url'}>
          <Div flex={{
          align: 'center',
          gap: 3
        }}>
            <input checked={trigger_option === 'copy_url'} value="copy_url" name="trigger_option" type="radio" onChange={() => setTriggerOption('copy_url')} />
            <Text semi>Copy and paste source URL</Text>
          </Div>
          <Text size="xs" muted m={{
          t: 1
        }}>
            Bring Hookdeck source URL to your request producer. Higher effort, more functional.
          </Text>
        </StyledRadioCard>
        <StyledRadioCard as="label" p={3} checked={trigger_option === 'mock_request'}>
          <Div flex={{
          align: 'center',
          gap: 3
        }}>
            <input checked={trigger_option === 'mock_request'} value="mock_request" name="trigger_option" type="radio" onChange={() => setTriggerOption('mock_request')} />
            <Text semi>Test using a mock request</Text>
          </Div>
          <Text size="xs" muted m={{
          t: 1
        }}>
            Send a dummy request to Hookdeck source URL. Lower effort, best for a quick demo.
          </Text>
        </StyledRadioCard>
      </Div>

      {trigger_option === 'copy_url' && <Div flex={{
      direction: 'column',
      gap: 8
    }}>
          <Div flex={{
        gap: 3
      }}>
            <Div>
              <Badge neutral>1</Badge>
            </Div>
            <Div flex={{
          grow: true,
          direction: 'column'
        }}>
              <Text semi>Bring Hookdeck source URL to your webhook producer</Text>
              <Text muted m={{
            b: 4
          }}>
                Paste this Hookdeck source URL — which you received in your terminal in the previous
                step — as an endpoint in the service producing your requests.
              </Text>
              <CopyableField value={source_url} mono wrap m={{
            b: 6
          }} />
              <StyledCard overflow_hidden>
                <StyledCardSection muted p={{
              x: 8,
              t: 8
            }} style={{
              lineHeight: 0
            }}>
                  <img width={480} src={`/images/paste_url_illustration_${theme.mode}.png`} alt="Copy URL" />
                </StyledCardSection>
              </StyledCard>
            </Div>
          </Div>
          <Div flex={{
        gap: 3
      }}>
            <Div>
              <Badge neutral>2</Badge>
            </Div>
            <Div flex={{
          grow: true,
          direction: 'column'
        }}>
              <Text semi>Trigger event in your request producer</Text>
              <Text muted m={{
            b: 4
          }}>
                Fire an event that will send webhooks to the Hookdeck source URL.
              </Text>
              <StyledCard overflow_hidden>
                <StyledCardSection muted p={{
              x: 8,
              t: 8
            }} style={{
              lineHeight: 0
            }}>
                  <img width={480} src={`/images/trigger_event_illustration_${theme.mode}.png`} alt="Trigger Event" />
                </StyledCardSection>
              </StyledCard>
            </Div>
          </Div>
        </Div>}

      {trigger_option === 'mock_request' && <Div>
          <StyledCard overflow_hidden>
            <StyledCardSection>
              <Tabs tabs={[{
            key: 'web_ui',
            label: 'Web UI',
            help_tooltip: <Text size="s">Send a mock request within this flow.</Text>
          }, {
            key: 'curl',
            label: 'cURL (via terminal)',
            help_tooltip: <Text size="s">Send a mock request via cURL, using your terminal.</Text>
          }]} active_tab={request_format} onTabSelected={key => setRequestFormat(key as 'web_ui' | 'curl')} border={false} compact m={{
            t: 2,
            x: 3
          }} />
            </StyledCardSection>
            <StyledCardSection>
              {request_format === 'web_ui' && <Div>
                  <Editor language={'json'} value={mock_body} height="108px" onChange={setMockBody} display="minimal" />
                </Div>}

              {request_format === 'curl' && <Div>
                  <CopyableField value={curl_command} mono variant="command_list" borderless wrap p={3} />
                </Div>}
            </StyledCardSection>
          </StyledCard>

          {request_format === 'web_ui' && <Div m={{
        t: 8
      }}>
              <Button data-testid="guided-cli-send-mock-request" primary icon={is_sending ? 'loading' : 'send'} onClick={sendMockRequest} disabled={is_sending}>
                Send mock request
              </Button>
            </Div>}
        </Div>}

      {(trigger_option === 'copy_url' || request_format === 'curl') && <Div m={{
      t: 8
    }}>
          <Button outline onClick={nextStep}>
            Next
          </Button>
        </Div>}
    </Div>;
};
export default TriggerEvent;